import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES } from "@/lib/site";

function ServiceRow({ service, index, onNavigate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const reversed = index % 2 === 1;

  return (
    <div
      ref={ref}
      className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center py-16 md:py-24 border-t border-white/10"
      data-testid={`service-row-${service.id}`}
    >
      <motion.div
        initial={{ opacity: 0, clipPath: "inset(12% 12% 12% 12%)" }}
        whileInView={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className={`relative overflow-hidden aspect-[4/3] border border-white/10 ${
          reversed ? "lg:order-2" : ""
        }`}
      >
        <motion.img
          style={{ y: imgY }}
          src={service.image}
          alt={service.title}
          className="absolute inset-0 h-[125%] w-full object-cover will-change-transform"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <span className="absolute top-5 left-5 font-serif text-5xl md:text-6xl text-gold/90">
          {service.num}
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={reversed ? "lg:order-1" : ""}
      >
        <h3 className="font-serif font-light text-4xl md:text-5xl text-white leading-tight">
          {service.title}
        </h3>
        <p className="mt-6 text-base md:text-lg font-light text-white/60 leading-relaxed max-w-lg">
          {service.desc}
        </p>
        <ul className="mt-8 space-y-3">
          {service.points.map((p) => (
            <li key={p} className="flex items-center gap-3 text-white/80">
              <Check size={16} className="text-gold shrink-0" />
              <span className="text-sm tracking-wide">{p}</span>
            </li>
          ))}
        </ul>
        <button
          onClick={() => onNavigate("#booking")}
          className="group mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold hover:text-gold-light transition-colors duration-300"
          data-testid={`service-book-${service.id}`}
        >
          Book this service
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </button>
      </motion.div>
    </div>
  );
}

export default function Services({ onNavigate }) {
  return (
    <section id="services" className="relative bg-[#0A0A0A] py-24 md:py-32" data-testid="services-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4"
        >
          <div>
            <p className="mb-6 text-xs uppercase tracking-mega text-gold">What We Do</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl text-white tracking-tight leading-[0.95]">
              The Services
            </h2>
          </div>
          <p className="max-w-sm text-sm font-light text-white/50 leading-relaxed">
            Four disciplines, one obsessive standard. From speed boats and power boats to yachts —
            choose a single detail or a season-long plan.
          </p>
        </motion.div>

        {SERVICES.map((s, i) => (
          <ServiceRow key={s.id} service={s} index={i} onNavigate={onNavigate} />
        ))}
      </div>
    </section>
  );
}
